/* eslint-disable lingui/no-unlocalized-strings */
/**
 * Centralized Tenant Configuration
 *
 * ⚠️ **IMPORTANT**: This is a NEW configuration system that is NOT YET IN USE.
 * The existing tenant configuration system (envs.js, config/index.ts, etc.)
 * is still the active system. This new structure is being set up for future migration.
 *
 * This file is the single source of truth for all tenant-specific configuration.
 * When adding a new tenant, simply add a new entry to the TENANTS array below.
 *
 * Security Note: This file contains configuration for ALL tenants. At deploy time,
 * only the configuration for the target environment will be included in the bundle
 * to prevent data leakage across tenants.
 *
 * @see ME-427592 - Redo env config setup
 */

import { launchDarklyKeyMap } from '../launchDarklyKeyMap';

const EMPTY_ACCT_UUID = '00000000-0000-0000-0000-000000000000'; //empty

/**
 * Tenant configuration interface
 */
export interface AppTenantConfig {
  /** Unique tenant ID (e.g., 'mm100', 'ml100', 'bi50n') */
  tenantId: string;

  /** Human-readable tenant name (e.g., 'Mastery', 'Molo', 'Bison') */
  displayName: string;

  /** Subscription name for the tenant */
  subscription: string;

  /** Environments for this tenant */
  environments: TenantEnvironment[];
}

/**
 * Environment-specific configuration
 */
export interface TenantEnvironment {
  /** Environment key (e.g., 'mm100-dev', 'ml100-prod') */
  envKey: string;

  /** Environment type */
  type: 'dev' | 'test' | 'prod';

  /** Primary hostname */
  hostname: string;

  /** Alias hostnames (optional) */
  aliases?: string[];

  /** Azure CDN bucket name */
  bucket: string;

  /** New Relic Application ID */
  applicationId: string;

  /** New Relic Agent ID (defaults to applicationId if not specified) */
  agentId?: string;

  /** FlatFile Platform Publishable Key */
  flatFilePublishableKey: string;

  /** FlatFile Platform Environment ID */
  flatFileEnvironmentId: string;

  /** FlatFile V1 License Key (UUID) */
  flatFileLicenseKey: string;

  /** LaunchDarkly key for this environment (optional, defaults to tenant-level) */
  launchDarklyKey?: string;
}

/**
 * Complete tenant registry
 *
 * To add a new tenant:
 * 1. Add a new entry to this array
 * 2. Fill in all required configuration
 * 3. Run builds - only relevant data will be included per environment
 */
export const TENANTS: AppTenantConfig[] = [
  // ==========================================
  // MASTERY INTERNAL TENANTS
  // ==========================================
  {
    tenantId: 'mm100',
    displayName: 'Mastery',
    subscription: 'masteryinternal',
    environments: [
      {
        envKey: 'mm100-dev',
        type: 'dev',
        hostname: 'dev.mm100.mastermindtms.com',
        aliases: [],
        bucket: 'stcdnmm100dev',
        applicationId: '1027981809',
        flatFilePublishableKey: 'pk_94ba8fbf2fde4568a3c20f910b69fd83',
        flatFileEnvironmentId: 'us_env_v2SeUhZx',
        flatFileLicenseKey: '70cbe3fa-b6ba-4943-be91-c2b93060c496',
      },
    ],
  },
  {
    tenantId: 'mit01',
    displayName: 'Tango',
    subscription: 'masteryinternaltest',
    environments: [
      {
        envKey: 'mit01-test',
        type: 'test',
        hostname: 'test.tango.mastermindtms.com',
        bucket: 'stcdnmit01test',
        applicationId: '1120367621',
        flatFilePublishableKey: 'pk_1b2ee6a16f5544a3b4d435ddedf4f1f9',
        flatFileEnvironmentId: 'us_env_MSsWMym7',
        flatFileLicenseKey: '70cbe3fa-b6ba-4943-be91-c2b93060c496',
      },
    ],
  },
  {
    tenantId: 'mdem0',
    displayName: 'Demo',
    subscription: 'masterydemo',
    environments: [
      {
        envKey: 'mdem0-prod',
        type: 'prod',
        hostname: 'demo.mastermindtms.com',
        bucket: 'stcdnmdem0prod',
        applicationId: '1120279881',
        flatFilePublishableKey: 'pk_94ba8fbf2fde4568a3c20f910b69fd83',
        flatFileEnvironmentId: 'us_env_v2SeUhZx',
        flatFileLicenseKey: '43858bc8-52d8-4a5e-98b2-8cc49342ed6b',
      },
    ],
  },

  // ==========================================
  // V1 CLIENT TENANTS
  // ==========================================
  {
    tenantId: 'ml100',
    displayName: 'Molo',
    subscription: 'molo',
    environments: [
      {
        envKey: 'ml100-test',
        type: 'test',
        hostname: 'test.ml100.mastermindtms.com',
        aliases: ['sandbox.shipmolo.com', 'upsidedown.shipmolo.com'],
        bucket: 'stcdnml100test',
        applicationId: '781278364',
        flatFilePublishableKey: 'pk_66bfd2945e2c40dd917e7e0f6a0dcfc9',
        flatFileEnvironmentId: 'us_env_YcYV2UZt',
        flatFileLicenseKey: '14499380-d8b8-43b8-9108-df4623a59cde',
      },
      {
        envKey: 'ml100-prod',
        type: 'prod',
        hostname: 'mm.shipmolo.com',
        aliases: ['ml100.mastermindtms.com', 'molo.mastermindtms.com'],
        bucket: 'stcdnml100prod',
        applicationId: '781280210',
        flatFilePublishableKey: 'pk_2abf6be87da34e73953c983f538e74f9',
        flatFileEnvironmentId: 'us_env_02r4NbQy',
        flatFileLicenseKey: '14499380-d8b8-43b8-9108-df4623a59cde',
      },
    ],
  },
  {
    tenantId: 'sn100',
    displayName: 'Schneider',
    subscription: 'schneider',
    environments: [
      {
        envKey: 'sn100-dev',
        type: 'dev',
        hostname: 'dev.sn100.mastermindtms.com',
        bucket: 'stcdnsn100dev',
        applicationId: '781287499',
        flatFilePublishableKey: 'pk_a211addd1ff84ad3bd52acf64dcc1ccf',
        flatFileEnvironmentId: 'us_env_6oK7l78g',
        flatFileLicenseKey: '90c6e4fe-8f73-4957-b5cf-9e4a86a232f3',
      },
      {
        envKey: 'sn100-test',
        type: 'test',
        hostname: 'test.sn100.mastermindtms.com',
        bucket: 'stcdnsn100test',
        applicationId: '781333756',
        flatFilePublishableKey: 'pk_a211addd1ff84ad3bd52acf64dcc1ccf',
        flatFileEnvironmentId: 'us_env_6oK7l78g',
        flatFileLicenseKey: '90c6e4fe-8f73-4957-b5cf-9e4a86a232f3',
      },
      {
        envKey: 'sn100-prod',
        type: 'prod',
        hostname: 'sn100.mastermindtms.com',
        bucket: 'stcdnsn100prod',
        applicationId: '781336609',
        flatFilePublishableKey: 'pk_b16ddd93b537473ab137212bef191b31',
        flatFileEnvironmentId: 'us_env_RcObW66u',
        flatFileLicenseKey: '90c6e4fe-8f73-4957-b5cf-9e4a86a232f3',
      },
    ],
  },
  {
    tenantId: 'we100',
    displayName: 'Werner',
    subscription: 'werner',
    environments: [
      {
        envKey: 'we100-dev',
        type: 'dev',
        hostname: 'dev.we100.mastermindtms.com',
        bucket: 'stcdnwe100dev',
        applicationId: '781287498',
        flatFilePublishableKey: 'pk_84535934b3614cd48875069329816239',
        flatFileEnvironmentId: 'us_env_Y4ht7LrH',
        flatFileLicenseKey: '9a3b33e2-6d7b-4c6c-82cc-79fe7af5cad8',
      },
      {
        envKey: 'we100-test',
        type: 'test',
        hostname: 'test.we100.mastermindtms.com',
        bucket: 'stcdnwe100test',
        applicationId: '781340459',
        flatFilePublishableKey: 'pk_84535934b3614cd48875069329816239',
        flatFileEnvironmentId: 'us_env_Y4ht7LrH',
        flatFileLicenseKey: '9a3b33e2-6d7b-4c6c-82cc-79fe7af5cad8',
      },
      {
        envKey: 'we100-prod',
        type: 'prod',
        hostname: 'we100.mastermindtms.com',
        bucket: 'stcdnwe100prod',
        applicationId: '781341436',
        flatFilePublishableKey: 'pk_49202fde45074df0b902ddd3fc159370',
        flatFileEnvironmentId: 'us_env_GO44mgHF',
        flatFileLicenseKey: '9a3b33e2-6d7b-4c6c-82cc-79fe7af5cad8',
      },
    ],
  },
  {
    tenantId: 'ru100',
    displayName: 'Ruan',
    subscription: 'ruan',
    environments: [
      {
        envKey: 'ru100-test',
        type: 'test',
        hostname: 'test.ru100.mastermindtms.com',
        bucket: 'stcdnru100test',
        applicationId: '1027939934',
        flatFilePublishableKey: 'pk_5f66f323a4114b139ab183c7ae1860d6',
        flatFileEnvironmentId: 'us_env_2ToOqqXi',
        flatFileLicenseKey: '2e6c4dcc-429a-4413-ba15-578b2fcd562c',
      },
      {
        envKey: 'ru100-prod',
        type: 'prod',
        hostname: 'ru100.mastermindtms.com',
        bucket: 'stcdnru100prod',
        applicationId: '1120018054',
        flatFilePublishableKey: 'pk_2e6d712a30344d8383aacbc69362cd6a',
        flatFileEnvironmentId: 'us_env_lroyjjB8',
        flatFileLicenseKey: '2e6c4dcc-429a-4413-ba15-578b2fcd562c',
      },
    ],
  },
  {
    tenantId: 'pr100',
    displayName: 'Pitt Ohio',
    subscription: 'prime',
    environments: [
      {
        envKey: 'pr100-test',
        type: 'test',
        hostname: 'test.pr100.mastermindtms.com',
        bucket: 'stcdnpr100test',
        applicationId: '1120010817',
        flatFilePublishableKey: 'pk_9371cfde9e4443da9a4050c99bc5c888',
        flatFileEnvironmentId: 'us_env_he5iLkUN',
        flatFileLicenseKey: '21ee849d-6b8d-48d0-b036-f89eb7b60799',
      },
      {
        envKey: 'pr100-prod',
        type: 'prod',
        hostname: 'pr100.mastermindtms.com',
        bucket: 'stcdnpr100prod',
        applicationId: '1120139265',
        flatFilePublishableKey: 'pk_424393b09a6f4615b54460ee3c8f533a',
        flatFileEnvironmentId: 'us_env_JoiAQidb',
        flatFileLicenseKey: '21ee849d-6b8d-48d0-b036-f89eb7b60799',
      },
    ],
  },

  // ==========================================
  // V2 CLIENT TENANTS
  // ==========================================
  {
    tenantId: 'ave4u',
    displayName: 'Averitt',
    subscription: 'averitt',
    environments: [
      {
        envKey: 'ave4u-test',
        type: 'test',
        hostname: 'test.averitt.mastermindtms.com',
        bucket: 'stcdnave4utest',
        applicationId: '1120117578',
        flatFilePublishableKey: 'pk_c9a6539877c149ba9abecadbefd3a769',
        flatFileEnvironmentId: 'us_env_vlhcGzZW',
        flatFileLicenseKey: '1c8e377d-83c8-41ad-9663-567ce868e9e8',
      },
      {
        envKey: 'ave4u-prod',
        type: 'prod',
        hostname: 'averitt.mastermindtms.com',
        bucket: 'stcdnave4uprod',
        applicationId: '1120185706',
        flatFilePublishableKey: 'pk_1372ef4479d84498a7d37a35f35c0c4c',
        flatFileEnvironmentId: 'us_env_Xe8u8epU',
        flatFileLicenseKey: '1c8e377d-83c8-41ad-9663-567ce868e9e8',
      },
    ],
  },
  {
    tenantId: 'co2yt',
    displayName: 'Covenant',
    subscription: 'covenant',
    environments: [
      {
        envKey: 'co2yt-test',
        type: 'test',
        hostname: 'test.covenant.mastermindtms.com',
        bucket: 'stcdnco2yttest',
        applicationId: '1120126085',
        flatFilePublishableKey: 'pk_479e480bdd374ca5845c07bf5dd62879',
        flatFileEnvironmentId: 'us_env_vg2oeXm4',
        flatFileLicenseKey: '4efb98af-d075-43e4-b6cd-941ea3854d81',
      },
      {
        envKey: 'co2yt-prod',
        type: 'prod',
        hostname: 'covenant.mastermindtms.com',
        bucket: 'stcdnco2ytprod',
        applicationId: '1120166467',
        flatFilePublishableKey: 'pk_29b75a97d416473aa735af3f7aa1b93c',
        flatFileEnvironmentId: 'us_env_Q9Ws0YLG',
        flatFileLicenseKey: '4efb98af-d075-43e4-b6cd-941ea3854d81',
      },
    ],
  },
  {
    tenantId: 'cgl60',
    displayName: 'Cargill',
    subscription: 'cargill',
    environments: [
      {
        envKey: 'cgl60-test',
        type: 'test',
        hostname: 'test.cargill.mastermindtms.com',
        bucket: 'stcdncgl60test',
        applicationId: '1120170727',
        flatFilePublishableKey: 'pk_74877c3bb32048359c88b8cb5e0e59ba',
        flatFileEnvironmentId: 'us_env_VRJvFpQ8',
        flatFileLicenseKey: '2447f3ef-9763-4e3d-97bc-a0b5c2d7ef8b',
      },
      {
        envKey: 'cgl60-prod',
        type: 'prod',
        hostname: 'cargill.mastermindtms.com',
        bucket: 'stcdncgl60prod',
        applicationId: '1120264430',
        flatFilePublishableKey: 'pk_298336c5eedc4b1ea93b4ee92c6b4afc',
        flatFileEnvironmentId: 'us_env_hU5tiRAr',
        flatFileLicenseKey: '2447f3ef-9763-4e3d-97bc-a0b5c2d7ef8b',
      },
    ],
  },
  {
    tenantId: 'eva9o',
    displayName: 'Evans',
    subscription: 'evans',
    environments: [
      {
        envKey: 'eva9o-test',
        type: 'test',
        hostname: 'test.evans.mastermindtms.com',
        bucket: 'stcdneva9otest',
        applicationId: '1120176258',
        flatFilePublishableKey: 'pk_ab7fe7bb493044eeb109fbe5827d7da8',
        flatFileEnvironmentId: 'us_env_Hi26MerC',
        flatFileLicenseKey: 'cd04a10e-324b-485d-93bf-7f8108be0d7a',
      },
      {
        envKey: 'eva9o-prod',
        type: 'prod',
        hostname: 'evans.mastermindtms.com',
        bucket: 'stcdneva9oprod',
        applicationId: '1120231611',
        flatFilePublishableKey: 'pk_af8aa2ede879474ebc6a567b43adb607',
        flatFileEnvironmentId: 'us_env_yGee7FKA',
        flatFileLicenseKey: 'cd04a10e-324b-485d-93bf-7f8108be0d7a',
      },
    ],
  },
  {
    tenantId: 'hir3j',
    displayName: 'Hirschbach',
    subscription: 'hirschbach',
    environments: [
      {
        envKey: 'hir3j-prod',
        type: 'prod',
        hostname: 'hirschbach.mastermindtms.com',
        bucket: 'stcdnhir3jprod',
        applicationId: '1120197169',
        flatFilePublishableKey: 'pk_fae9d5f7c3744a52ad171ab20b06adcd',
        flatFileEnvironmentId: 'us_env_3CSt2UNl',
        flatFileLicenseKey: 'be91054c-bf19-445d-b8f5-2ca459808bf1',
      },
    ],
  },
  {
    tenantId: 'cat7x',
    displayName: 'CAT',
    subscription: 'cat',
    environments: [
      {
        envKey: 'cat7x-test',
        type: 'test',
        hostname: 'test.cat.mastermindtms.com',
        bucket: 'stcdncat7xtest',
        applicationId: '1120221736',
        flatFilePublishableKey: 'pk_4919d020472e461c9a6a0cc78860eaa3',
        flatFileEnvironmentId: 'us_env_5DQMI9Bh',
        flatFileLicenseKey: '19eafb0a-81d4-4ea1-921a-26772d606d45',
      },
      {
        envKey: 'cat7x-prod',
        type: 'prod',
        hostname: 'cat.mastermindtms.com',
        bucket: 'stcdncat7xprod',
        applicationId: '1120264445',
        flatFilePublishableKey: 'pk_fc1a869b065e4fc2b46e582950e20cf3',
        flatFileEnvironmentId: 'us_env_Yv1h06H9',
        flatFileLicenseKey: '19eafb0a-81d4-4ea1-921a-26772d606d45',
      },
    ],
  },
  {
    tenantId: 'dg3nl',
    displayName: 'Dollar General',
    subscription: 'dollargeneral',
    environments: [
      {
        envKey: 'dg3nl-test',
        type: 'test',
        hostname: 'test.dollargeneral.mastermindtms.com',
        bucket: 'stcdndg3nltest',
        applicationId: '1120231610',
        flatFilePublishableKey: 'pk_32fc2f5c1ceb47eba98ef0bcdd2a09e6',
        flatFileEnvironmentId: 'us_env_PPbaZqj3',
        flatFileLicenseKey: '61a2639f-3e08-416a-89cc-1ddfa9f92c67',
      },
      {
        envKey: 'dg3nl-prod',
        type: 'prod',
        hostname: 'dollargeneral.mastermindtms.com',
        bucket: 'stcdndg3nlprod',
        applicationId: '1120294371',
        flatFilePublishableKey: 'pk_ed13240126fb40a982115e8e5d6a1ab0',
        flatFileEnvironmentId: 'us_env_RsqOI6Py',
        flatFileLicenseKey: '61a2639f-3e08-416a-89cc-1ddfa9f92c67',
      },
    ],
  },
  {
    tenantId: 'kdp5t',
    displayName: 'KDP',
    subscription: 'kdp',
    environments: [
      {
        envKey: 'kdp5t-test',
        type: 'test',
        hostname: 'test.kdp.mastermindtms.com',
        bucket: 'stcdnkdp5ttest',
        applicationId: '1120251709',
        flatFilePublishableKey: 'pk_dfad5a6b59ec4d74a0fd6475d4e1029a',
        flatFileEnvironmentId: 'us_env_1zdbgX0S',
        flatFileLicenseKey: '6674d3bf-296e-41a9-bd34-a0909806dca7',
      },
      {
        envKey: 'kdp5t-prod',
        type: 'prod',
        hostname: 'kdp.mastermindtms.com',
        bucket: 'stcdnkdp5tprod',
        applicationId: '1120276831',
        flatFilePublishableKey: 'pk_8b34db3b39b443eb80d83b1e9c650215',
        flatFileEnvironmentId: 'us_env_FwFahtj0',
        flatFileLicenseKey: '6674d3bf-296e-41a9-bd34-a0909806dca7',
      },
    ],
  },
  {
    tenantId: 'pep5c',
    displayName: 'Pepsi',
    subscription: 'pepsi',
    environments: [
      {
        envKey: 'pep5c-test',
        type: 'test',
        hostname: 'test.pepsi.mastermindtms.com',
        bucket: 'stcdnpep5ctest',
        applicationId: '1120270028',
        flatFilePublishableKey: 'pk_8bbcd6c3f757453691ada4fb8afa7a30',
        flatFileEnvironmentId: 'us_env_129u1W2w',
        flatFileLicenseKey: '3bf9db91-4ddf-4165-9f01-e834c26c97c6',
      },
      {
        envKey: 'pep5c-prod',
        type: 'prod',
        hostname: 'pepsi.mastermindtms.com',
        bucket: 'stcdnpep5cprod',
        applicationId: '1120302517',
        flatFilePublishableKey: 'pk_057c5a61506a4d3f8e8bb54f432ce0bf',
        flatFileEnvironmentId: 'us_env_hAWpjlV4',
        flatFileLicenseKey: '3bf9db91-4ddf-4165-9f01-e834c26c97c6',
      },
    ],
  },
  {
    tenantId: 'usmmg',
    displayName: 'USMMG',
    subscription: 'multimodalgroup',
    environments: [
      {
        envKey: 'usmmg-test',
        type: 'test',
        hostname: 'test.usmmg.mastermindtms.com',
        bucket: 'stcdnusmmgtest',
        applicationId: '1120297600',
        flatFilePublishableKey: 'pk_c32e83c792c84d9f963728c78c968be9',
        flatFileEnvironmentId: 'us_env_bdw1AYMX',
        flatFileLicenseKey: 'ee8cd5cf-15bb-4bd5-9274-ad117a207b9a',
      },
      {
        envKey: 'usmmg-prod',
        type: 'prod',
        hostname: 'usmmg.mastermindtms.com',
        bucket: 'stcdnusmmgprod',
        applicationId: '1120309374',
        flatFilePublishableKey: 'pk_984f9d7a94cf4d3a9fe420c5d691f6f8',
        flatFileEnvironmentId: 'us_env_07EFCAfa',
        flatFileLicenseKey: 'ee8cd5cf-15bb-4bd5-9274-ad117a207b9a',
      },
    ],
  },
  {
    tenantId: 'chs9l',
    displayName: 'CHS',
    subscription: 'chs',
    environments: [
      {
        envKey: 'chs9l-test',
        type: 'test',
        hostname: 'test.chs.mastermindtms.com',
        bucket: 'stcdnchs9ltest',
        applicationId: '1120350486',
        flatFilePublishableKey: 'pk_352fd178f66b4bbebf6e491e464fcf80',
        flatFileEnvironmentId: 'us_env_97NEpx7l',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'chs9l-prod',
        type: 'prod',
        hostname: 'chs.mastermindtms.com',
        bucket: 'stcdnchs9lprod',
        applicationId: '1120350504',
        flatFilePublishableKey: 'pk_2f9b7f8118c049749493bf05e1259e55',
        flatFileEnvironmentId: 'us_env_IX8hPiMv',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'bi50n',
    displayName: 'Bison',
    subscription: 'bison',
    environments: [
      {
        envKey: 'bi50n-test',
        type: 'test',
        hostname: 'test.bison.mastermindtms.com',
        bucket: 'stcdnbi50ntest',
        applicationId: '1120378001',
        flatFilePublishableKey: 'pk_3f153c612a894d8087c4e47cc318faed',
        flatFileEnvironmentId: 'us_env_URE7Jhme',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'bi50n-prod',
        type: 'prod',
        hostname: 'bison.mastermindtms.com',
        bucket: 'stcdnbi50nprod',
        applicationId: '1120420570',
        flatFilePublishableKey: 'pk_c4dbb48fc4f245828a84c0a0e74ce021',
        flatFileEnvironmentId: 'us_env_tMrpK11V',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'av36p',
    displayName: 'Avenue',
    subscription: 'avenue',
    environments: [
      {
        envKey: 'av36p-test',
        type: 'test',
        hostname: 'test.avenue.mastermindtms.com',
        bucket: 'stcdnav36ptest',
        applicationId: '1120429764',
        flatFilePublishableKey: 'pk_d12e1202a34a41a3a16fa37239949d08',
        flatFileEnvironmentId: 'us_env_tIu9bJg5',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'av36p-prod',
        type: 'prod',
        hostname: 'avenue.mastermindtms.com',
        bucket: 'stcdnav36pprod',
        applicationId: '1120434349',
        flatFilePublishableKey: 'pk_f6feb6a4237b40969535ddfb4d450af9',
        flatFileEnvironmentId: 'us_env_uy8Zae2i',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'cra73',
    displayName: 'Crane',
    subscription: 'crane',
    environments: [
      {
        envKey: 'cra73-test',
        type: 'test',
        hostname: 'test.crane.mastermindtms.com',
        bucket: 'stcdncra73test',
        applicationId: '1120475143',
        flatFilePublishableKey: 'pk_426eea35dce547cf8ead84db1929ed30',
        flatFileEnvironmentId: 'us_env_hbYAWmnd',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'cra73-prod',
        type: 'prod',
        hostname: 'crane.mastermindtms.com',
        bucket: 'stcdncra73prod',
        applicationId: '1120527124',
        flatFilePublishableKey: 'pk_6da7ca3829a142609bf46891c8cf492f',
        flatFileEnvironmentId: 'us_env_4SJxugB2',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'ryd35',
    displayName: 'Ryder',
    subscription: 'ryder',
    environments: [
      {
        envKey: 'ryd35-test',
        type: 'test',
        hostname: 'test.ryder.mastermindtms.com',
        bucket: 'stcdnryd35test',
        applicationId: '1120508173',
        flatFilePublishableKey: 'pk_9d792676825c49c4942bc8609a1e176f',
        flatFileEnvironmentId: 'us_env_qCC9e0vz',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'ryd35-prod',
        type: 'prod',
        hostname: 'ryder.mastermindtms.com',
        bucket: 'stcdnryd35prod',
        applicationId: '1120508173',
        flatFilePublishableKey: 'pk_PRODUCTION_KEY_FOR_RYDER',
        flatFileEnvironmentId: 'NEED PROD ENV ID',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'rcl82',
    displayName: 'Red Classic',
    subscription: 'redclassic',
    environments: [
      {
        envKey: 'rcl82-test',
        type: 'test',
        hostname: 'test.redclassic.mastermindtms.com',
        bucket: 'stcdnrcl82test',
        applicationId: '1120538327',
        flatFilePublishableKey: 'pk_be4b6b8d431546d9b7c9be630293422b',
        flatFileEnvironmentId: 'us_env_Im9ImLZb',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'rcl82-prod',
        type: 'prod',
        hostname: 'redclassic.mastermindtms.com',
        bucket: 'stcdnrcl82prod',
        applicationId: '1120538327',
        flatFilePublishableKey: 'pk_PRODUCTION_KEY_FOR_REDCLASSIC',
        flatFileEnvironmentId: 'NEED PROD ENV ID',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'connect-carrier',
    displayName: 'Connect Carrier',
    subscription: 'hub',
    environments: [
      {
        envKey: 'dev',
        type: 'dev',
        hostname: 'connect-carrier.dev.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'test',
        type: 'test',
        hostname: 'connect-carrier.test.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'uat-e',
        type: 'test',
        hostname: 'connect-carrier.uat-e.hub.mastermindtms.com',
        aliases: ['connect-carrier.uat-c.hub.mastermindtms.com'],
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'prod',
        type: 'prod',
        hostname: 'connect-carrier.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
  {
    tenantId: 'connect-customer',
    displayName: 'Connect Customer',
    subscription: 'hub',
    environments: [
      {
        envKey: 'connect-customer.hub-dev',
        type: 'dev',
        hostname: 'connect-customer.dev.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'connect-customer.hub-test',
        type: 'test',
        hostname: 'connect-customer.test.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'connect-customer.uat-e.hub-prod',
        type: 'test',
        hostname: 'connect-customer.uat-e.hub.mastermindtms.com',
        aliases: ['connect-customer.uat-c.hub.mastermindtms.com'],
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
      {
        envKey: 'connect-customer.hub-prod',
        type: 'prod',
        hostname: 'connect-customer.hub.mastermindtms.com',
        bucket: '',
        applicationId: '',
        flatFilePublishableKey: '',
        flatFileEnvironmentId: '',
        flatFileLicenseKey: EMPTY_ACCT_UUID,
      },
    ],
  },
];

/**
 * Helper to find a tenant by ID
 */
export const findTenantById = (tenantId: string): AppTenantConfig | undefined => {
  return TENANTS.find((t) => t.tenantId === tenantId);
};

const withResolvedLaunchDarklyKey = (
  environment: TenantEnvironment
): TenantEnvironment => {
  if (environment.launchDarklyKey) {
    return environment;
  }

  // Keep parity with active config behavior when launchDarklyKey is omitted.
  if (environment.envKey === 'mm100-dev') {
    return { ...environment, launchDarklyKey: launchDarklyKeyMap.internalDev };
  }
  if (environment.envKey === 'mit01-test') {
    return { ...environment, launchDarklyKey: launchDarklyKeyMap.internalTest };
  }

  // Connect apps have special LD key logic matching legacy behavior
  const tenantId = TENANTS.find((t) =>
    t.environments.some((e) => e.envKey === environment.envKey)
  )?.tenantId;
  if (tenantId === 'connect-carrier' || tenantId === 'connect-customer') {
    if (environment.type === 'dev') {
      return { ...environment, launchDarklyKey: launchDarklyKeyMap.internalDev };
    }
    if (environment.type === 'test') {
      return { ...environment, launchDarklyKey: launchDarklyKeyMap.internalTest };
    }
    // For connect apps, check if hostname contains 'uat' to determine preview vs production
    if (environment.hostname.includes('uat')) {
      return { ...environment, launchDarklyKey: launchDarklyKeyMap.preview };
    }
    return { ...environment, launchDarklyKey: launchDarklyKeyMap.production };
  }

  // Standard tenants: dev/test use preview, prod uses production
  if (environment.type === 'dev' || environment.type === 'test') {
    return { ...environment, launchDarklyKey: launchDarklyKeyMap.preview };
  }
  return { ...environment, launchDarklyKey: launchDarklyKeyMap.production };
};

/**
 * Helper to find an environment by key
 */
export const findEnvironmentByKey = (
  envKey: string
): { tenant: AppTenantConfig; environment: TenantEnvironment } | undefined => {
  for (const tenant of TENANTS) {
    const environment = tenant.environments.find((e) => e.envKey === envKey);
    if (environment) {
      return { tenant, environment: withResolvedLaunchDarklyKey(environment) };
    }
  }
  return undefined;
};

/**
 * Helper to find an environment by hostname
 */
export const findEnvironmentByHostname = (
  hostname: string
): { tenant: AppTenantConfig; environment: TenantEnvironment } | undefined => {
  for (const tenant of TENANTS) {
    for (const environment of tenant.environments) {
      if (
        environment.hostname === hostname ||
        environment.aliases?.includes(hostname)
      ) {
        return {
          tenant,
          environment: withResolvedLaunchDarklyKey(environment),
        };
      }
    }
  }
  return undefined;
};

/**
 * Get all environment keys (for deployment workflows)
 */
export const getAllEnvironmentKeys = (): string[] => {
  return TENANTS.flatMap((t) => t.environments.map((e) => e.envKey));
};

/**
 * Get all hostnames
 */
export const getAllHostnames = (): string[] => {
  const hostnames: string[] = [];
  for (const tenant of TENANTS) {
    for (const environment of tenant.environments) {
      hostnames.push(environment.hostname);
      if (environment.aliases) {
        hostnames.push(...environment.aliases);
      }
    }
  }
  return hostnames;
};
